import MapWithDrawing from "../shared/components/MapWithDrawing";
import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {MapCameraChangedEvent} from "@vis.gl/react-google-maps";
import {Link, useLocation, useNavigate} from "react-router-dom";
import {customerService} from "../services/customerService";
import {
    getAverageCenterOfPolygon,
    getBusinessOrCustomerName,
    GetGoogleMapsCoordsFromAddress,
    round5
} from "../shared/sharedFunctions";
import {useForm} from "react-hook-form";
import {businessYearService} from "../services/businessYearService";
import notificationService from "../services/NotificationService";
import {google_api_key} from "../services/common.services";
import {estimateService} from "../services/EstimateService";
import NotifyAnywayModal from "../components/NotifyCustomerEstimateAnywayModal";
import ConfirmationModal from "../components/ConfirmationModal";
import PreviewEstimateModal from "../components/PreviewEstimateModal";
import {PopoverButton} from "../shared/components/PopoverButton";
import {Nav} from "react-bootstrap";
import eventEmitter from "../shared/eventEmitter";
import {EventEmitterTypes} from "../shared/eventEmitterEnum";
import {useLoading} from "../shared/components/LoadingContext";

const EstimateDetailsPage: React.FC = () => {
    const {state} = useLocation()
    const { register, setValue, reset, getValues, watch} = useForm();
    const navigate = useNavigate()
    const [mapDetails, setMapDetails] = useState(null);
    const [editSquareFeet, setEditSquareFeet] = useState(false);
    const [showNotifyAnyway, setShowNotifyAnyway] = useState(false);
    const [showPriceChangeConfirmation, setShowPriceChangeConfirmation] = useState(false);
    const [showPreview, setShowPreview] = useState(false);
    const [previewHtml, setPreviewHtml] = useState<string | null>(null);
    const [previewFailed, setPreviewFailed] = useState(false);
    const [showEstimateNotes, setShowEstimateNotes] = useState(false);
    const [location, setLocation] = useState({ lat: 42.1539, lng: -88.052183 });
    const lastRun = useRef<number>(0);
    const [initialArea, setInitialArea] = useState(0);
    const [polygon, setPolygon] = useState<google.maps.Polygon>(null);
    const [polygonPath, setPolygonPath] = useState<any>(null);
    const [defaultPolygonPath, setDefaultPolygonPath] = useState<any>(null);
    const [customerData, setCustomerData] = useState<any>(null);
    const [priceBrackets, setPriceBrackets] = useState<any>(null);
    const [jobData, setJobData] = useState<any>(null);
    const { setIsLoading } = useLoading();


    const [
        sealcoatingPrice,
        patchworkPrice,
        hotPatchworkPrice,
        sawCutPrice,
        stripingPrice,
        crackSealPrice,
        hotCrackSealPrice,
        totalArea,
        manualAdjustmentAmount
    ] = watch([
        'sealcoatingPrice',
        'patchworkPrice',
        'hotPatchworkPrice',
        'sawCutPrice',
        'stripingPrice',
        'crackSealPrice',
        'hotCrackSealPrice',
        'totalArea',
        'manualAdjustmentAmount'
    ]);
    function formatAddress(c: any): string {
        //unfortunately need to ignore standardized address as old data has incomplete stuff
        //if (c.standardizedAddress) return c.standardizedAddress;

        const streetParts = [
            c.streetNumber,
            c.streetPrefix,
            c.streetName,
            c.streetSuffix,
            c.streetPostDirection
        ]
            .filter(p => !!p)
            .join(" ");

        const cityStateZip = [
            c.city,
            c.state,
            c.postalCode
        ]
            .filter(p => !!p)
            .join(" ");

        return `${streetParts}, ${cityStateZip}`;
    }

    useEffect(() => {
        (async () => {
            if(state && state.customerId && state.jobId){
                setIsLoading(true)
                Promise.all([
                    customerService.getCustomerById(state.customerId),
                    customerService.getCustomerJobByJobId(state.customerId, state.jobId),
                    customerService.getMapArea(state.customerId)
                ]).then(async res => {
                    setCustomerData(res[0])
                    if (res[1]) {
                        const area = res[1].jobSquareFeet != null ? res[1].jobSquareFeet : res[1].customerProvidedSquareFeet
                        setInitialArea(res[1].jobSquareFeet != null ? res[1].jobSquareFeet : res[1].customerProvidedSquareFeet)
                        reset({...res[1], totalArea: area })
                        setJobData({...res[1], totalArea: area })
                        if (res[1].estimateNotes) setShowEstimateNotes(true);
                    }
                    if (res[2].vertices && res[2].vertices.length > 0) {
                        const polyPath = res[2].vertices.map(vert => {
                            return {lat: vert.lat, lng: vert.long}
                        })
                        setDefaultPolygonPath(polyPath)
                        setPolygonPath(polyPath)
                        setLocation(getAverageCenterOfPolygon(res[2].vertices.map(vert => {
                            return {lat: vert.lat, lng: vert.long}
                        })))
                    } else {
                        let address = formatAddress(res[0]);
                        try {
                            const coords = await GetGoogleMapsCoordsFromAddress(address);
                            setLocation(coords)
                        } catch (e) {
                            console.warn('Geocoding failed, using default location:', e);
                        }
                    }
                    setIsLoading(false)
                }).catch(() => {
                    setIsLoading(false)
                })

                const currentYear = await businessYearService.getCurrentBusinessYear();
                businessYearService.getPriceBrackets(currentYear.year).then(r => {
                    setPriceBrackets(r)
                })
            }
        })();
    }, [state]);

    function getCostForArea(){
        if(!priceBrackets) return
        const bracket = priceBrackets.find((b) =>
            totalArea >= b.minimumSquareFeet &&
            (b.maximumSquareFeet === null || totalArea <= b.maximumSquareFeet) &&
            b.propertyType === customerData.propertyType
        );

        if (!bracket) return null;
        const cost =
            bracket.fixedPrice != null
                ? bracket.fixedPrice
                : (bracket.pricePerSquareFoot ?? 0) * totalArea;

        setValue('sealcoatingPrice', Math.ceil(cost))

    }

    const isCompletedJob = jobData?.dateDone != null;

    useEffect(() => {
        if (!isCompletedJob) getCostForArea();
    }, [totalArea, priceBrackets, isCompletedJob]);

    const bracketTooltip = useMemo(() => {
        if (!priceBrackets || !totalArea || !customerData) return null;
        const bracket = priceBrackets.find((b) =>
            totalArea >= b.minimumSquareFeet &&
            (b.maximumSquareFeet === null || totalArea <= b.maximumSquareFeet) &&
            b.propertyType === customerData.propertyType
        );
        if (!bracket) return null;
        const maxLabel = bracket.maximumSquareFeet != null ? bracket.maximumSquareFeet : "+";
        if (bracket.fixedPrice != null) {
            return `${bracket.minimumSquareFeet}-${maxLabel} @ $${bracket.fixedPrice} (fixed)`;
        }
        return `${bracket.minimumSquareFeet}-${maxLabel} @ $${bracket.pricePerSquareFoot}/sqft`;
    }, [totalArea, priceBrackets, customerData]);
    const handleCameraChange = useCallback((ev: MapCameraChangedEvent) => {
        const now = Date.now();
        // only run at most every x ms
        if (now - lastRun.current >= 100) {
            lastRun.current = now;
            setMapDetails(ev.detail);
        }
    }, [setMapDetails]);
    const calculateArea = (poly: google.maps.Polygon) => {
        const areaMeters = google.maps.geometry.spherical.computeArea(poly.getPath());
        const areaFeet = areaMeters * 10.7639;
        setValue("totalArea", Math.ceil(areaFeet));
    };
    const handlePolygon = (poly: google.maps.Polygon) => {
        const path = poly.getPath().getArray().map(p => p.toJSON());
        calculateArea(poly);
        setPolygon(poly);
        setPolygonPath(path);
        google.maps.event.addListener(poly.getPath(), 'set_at', () => calculateArea(poly));
        google.maps.event.addListener(poly.getPath(), 'insert_at', () => calculateArea(poly));
        google.maps.event.addListener(poly, 'rightclick', (e: any) => {
            if (e.vertex != null) {
                poly.getPath().removeAt(e.vertex);
                calculateArea(poly);
            }
        });
    };


    const servicesEstimate = useMemo(() => {
        const sum =
            Number(sealcoatingPrice) +
            Number(patchworkPrice) +
            Number(hotPatchworkPrice) +
            Number(sawCutPrice) +
            Number(stripingPrice) +
            Number(crackSealPrice) +
            Number(hotCrackSealPrice);
        return round5(Math.ceil(sum));
    }, [
        sealcoatingPrice,
        patchworkPrice,
        hotPatchworkPrice,
        sawCutPrice,
        stripingPrice,
        crackSealPrice,
        hotCrackSealPrice,
    ]);

    const finalPrice = useMemo(() => {
        return servicesEstimate + (Number(manualAdjustmentAmount) || 0);
    }, [servicesEstimate, manualAdjustmentAmount]);

    const buildUrl = (width: number, height: number) => {
        const path = polygonPath
            .map(pt => `${pt.lat},${pt.lng}`)
            .join('|');

        const centerPoint = polygonPath.length > 0
            ? getAverageCenterOfPolygon(polygonPath)
            : mapDetails.center;
        const zoom   = mapDetails.zoom;

        const params = new URLSearchParams({
            center: `${centerPoint.lat},${centerPoint.lng}`,
            zoom:   zoom.toString(),
            size:   `${width}x${height}`,
            maptype: 'hybrid',
            path: [
                `fillcolor:0x00FFFF80`,
                `color:0x0000FF`,
                `weight:2`,
                path,
            ].join('|'),
            key: google_api_key,
        });

        return `https://maps.googleapis.com/maps/api/staticmap?${params}`;
    };

    const blobToBase64 = (blob: Blob): Promise<string> =>
        new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => {
                const dataUrl = reader.result as string;
                resolve(dataUrl.split(',')[1] || '');
            };
            reader.onerror = reject;
            reader.readAsDataURL(blob);
        });

    const MIN_SIDE = 128;
    const STEP = 32;
    const TARGET = 256_000;

    const capture = async (): Promise<string> => {
        if (!polygonPath || polygonPath.length < 3) {
            return '';
        }

        let side = 1440;
        let blob: Blob;

        do {
            const url = buildUrl(side, side);
            const res = await fetch(url);
            if (!res.ok) throw new Error(res.statusText);
            blob = await res.blob();

            if (blob.size > TARGET) {
                side = Math.max(MIN_SIDE, side - STEP);
            }
        } while (blob.size > TARGET && side > MIN_SIDE);

        return await blobToBase64(blob);
    };

    function priceOrNull(value: any): number | null {
        if (value === '' || value == null || isNaN(Number(value))) return null;
        return Number(value);
    }

    function buildEstimatePayload(sendCustomerEmail: boolean) {
        const all = getValues();
        return {
            sendCustomerEmail: sendCustomerEmail,
            jobSquareFeet: totalArea,
            patchworkPrice: priceOrNull(all.patchworkPrice),
            hotPatchworkPrice: priceOrNull(all.hotPatchworkPrice),
            sawCutPrice: priceOrNull(all.sawCutPrice),
            stripingPrice: priceOrNull(all.stripingPrice),
            crackSealPrice: priceOrNull(all.crackSealPrice),
            hotCrackSealPrice: priceOrNull(all.hotCrackSealPrice),
            manualAdjustmentAmount: Number(all.manualAdjustmentAmount) || 0,
            estimateNotes: all.estimateNotes || null,
        };
    }

    async function openEstimatePreview() {
        setPreviewHtml(null);
        setPreviewFailed(false);
        setShowPreview(true);
        const preview = await estimateService.previewEstimate(state.jobId, buildEstimatePayload(false));
        if (preview?.bodyHtml) setPreviewHtml(preview.bodyHtml);
        else setPreviewFailed(true);
    }

    async function submitEstimate(sendCustomerEmail: boolean) {
        if(totalArea != initialArea && (polygonPath && polygonPath.length > 0)){
            const base64Image = await capture()
            await customerService.saveMapBase64ByCustomerId(customerData.customerId, base64Image)
            await customerService.saveMapAreaByCustomerId(customerData.customerId, polygonPath)
        }
        estimateService.approveEstimate(state.jobId, buildEstimatePayload(sendCustomerEmail)).then(r => {
            if(!r) return;
            if(sendCustomerEmail) notificationService.success('Estimate Sent to Customer')
            else notificationService.success('Job Estimate Updated')
            eventEmitter.emit(EventEmitterTypes.REFRESH_INFO);
            
            if (state?.fromJobDetails) {
                navigate('/admin/job-details', {
                    state: {
                        customerId: state.customerId,
                        jobId: state.jobId,
                        propertyAddress: state.propertyAddress,
                        fromJobDetails: true,
                        propertyType: customerData?.propertyType,
                        homePhone: customerData?.homePhone,
                    },
                })
            } else {
                navigate('/admin/estimates')
            }
        })
    }

    useEffect(() => {
        setValue("totalArea", totalArea);
    }, [totalArea]);


    function getCustomerAddress() {
        return [
            customerData.streetNumber,
            customerData.streetPrefix,
            customerData.streetName,
            customerData.streetSuffix,
        ].filter(Boolean).join(' ');
    }

    return (
        <>
            <div className="container-fluid ptCustom estimatePage">

                <div className="row">
                    <div className="col-12 mt-1 mb-3">
                        <div className="card shadow-sm rounded-lg">
                            <div className="card-body p-4">

                                <h3 className="mb-2 pb-2 fw-semibold border-bottom border-light">
                                    Estimate Adjustments
                                </h3>

                                <div className="row g-2">
                                    <div className="col-lg col-md-3 col-sm-6 mb-3">
                                        <label className="form-label">Sealcoating</label>
                                        <input disabled {...register('sealcoatingPrice')} type="number" className="form-control form-control-sm" id="textInput"
                                               aria-describedby="textInput" placeholder="0"/>
                                    </div>
                                    <div className="col-lg col-md-3 col-sm-6 mb-3">
                                        <label className="form-label">Patchwork</label>
                                        <input {...register('patchworkPrice')} type="number" className="form-control form-control-sm" id="textInput"
                                               aria-describedby="textInput" placeholder="0"/>
                                    </div>
                                    <div className="col-lg col-md-3 col-sm-6 mb-3">
                                        <label className="form-label">Hot Patchwork</label>
                                        <input {...register('hotPatchworkPrice')} type="number" className="form-control form-control-sm" id="textInput"
                                               aria-describedby="textInput" placeholder="0"/>
                                    </div>
                                    <div className="col-lg col-md-3 col-sm-6 mb-3">
                                        <label className="form-label">Saw Cut</label>
                                        <input {...register('sawCutPrice')} type="number" className="form-control form-control-sm" id="textInput"
                                               aria-describedby="textInput" placeholder="0"/>
                                    </div>
                                    <div className="col-lg col-md-3 col-sm-6 mb-3">
                                        <label className="form-label">Striping</label>
                                        <input {...register('stripingPrice')} type="number" className="form-control form-control-sm" id="textInput"
                                               aria-describedby="textInput" placeholder="0"/>
                                    </div>
                                    <div className="col-lg col-md-3 col-sm-6 mb-3">
                                        <label className="form-label">Crackseal</label>
                                        <input {...register('crackSealPrice')} type="number" className="form-control form-control-sm" id="textInput"
                                               aria-describedby="textInput" placeholder="0"/>
                                    </div>
                                    <div className="col-lg col-md-3 col-sm-6 mb-3">
                                        <label className="form-label">Hot Crackseal</label>
                                        <input {...register('hotCrackSealPrice')} type="number" className="form-control form-control-sm" id="textInput"
                                               aria-describedby="textInput" placeholder="0"/>
                                    </div>
                                    <div className="col-lg col-md-3 col-sm-6 mb-3">
                                        <label className="form-label">Manual Adj</label>
                                        <input {...register('manualAdjustmentAmount', { valueAsNumber: true })} type="number" className="form-control form-control-sm" id="textInput"
                                               aria-describedby="textInput" placeholder="0"/>
                                    </div>
                                </div>

                                <div className="row g-2 mt-1">
                                    <div className="col-12">
                                        {!showEstimateNotes ? (
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-outline-secondary"
                                                onClick={() => setShowEstimateNotes(true)}
                                            >
                                                <i className="far fa-note-sticky"></i> Add Note to Estimate Email
                                            </button>
                                        ) : (
                                            <div>
                                                <label className="form-label">
                                                    Estimate Note
                                                </label>
                                                <textarea
                                                    {...register('estimateNotes')}
                                                    className="form-control form-control-sm"
                                                    rows={3}
                                                    maxLength={1000}
                                                    placeholder="e.g. This estimate assumes both lots are sealcoated"
                                                />
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-link text-danger p-0 mt-1"
                                                    onClick={() => { setValue('estimateNotes', ''); setShowEstimateNotes(false); }}
                                                >
                                                    Remove note
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>

                            </div>
                        </div>
                    </div>
                </div>

                <div className="row">

                    <div className="col-md-8">
                        <div className="card map-card shadow-sm rounded-lg p-4">
                            <div className="estimateInfo">

                                <div className="row mb-3">
                                    <div className="col-md-6">
                                        <div className="sqFt official">
                                            <div className="type">
                                                <div className="labelWithTooltip">
                                                    <span>Official Sq Ft{'  '}</span>
                                                    {bracketTooltip && (
                                                        <PopoverButton
                                                            tooltipContent={bracketTooltip}
                                                            iTagClass={"fa-solid fa-circle-question opacity-25"}
                                                            buttonClass={"p-0"}
                                                        />
                                                    )}
                                                </div>
                                                {!isCompletedJob && <a className='truckEdit' style={{cursor: 'pointer'}}
                                                   onClick={() => setEditSquareFeet(!editSquareFeet)}><i
                                                    className=" fa-regular fa-pencil"></i> edit</a>}
                                            </div>
                                            <div className="sqFtTotal">
                                                <input
                                                    disabled={!editSquareFeet}
                                                    type="number"
                                                    {...register("totalArea", {
                                                        valueAsNumber: true,
                                                    })}
                                                    className="form-control form-control-sm"
                                                    id="textInput"
                                                    aria-describedby="textInput"
                                                    placeholder="0"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="sqFt customer">
                                            <div className="type">
                                                <span className="">
                                                    Customer Provided
                                                </span>
                                            </div>
                                            <div className="sqFtTotal">
                                                <span>{jobData ? jobData.customerProvidedSquareFeet : 'N/A'}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="row map-row">
                                    <div className="col">
                                        <div className="mapContainer">
                                            <div className="mapCanvas">
                                                <MapWithDrawing defaultPolygonPath={defaultPolygonPath} handleCameraChange={handleCameraChange} location={location} handlePolygon={handlePolygon}/>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="col-md-4">
                        <div className="card shadow-sm rounded-lg p-4">
                            <div className="estimateReview">
                                <div className="type">
                                    Customer Information
                                </div>
                                <div className="estFields">
                                    <div className="fieldPair">
                                        <span className="fieldLabel">
                                            Name
                                        </span>
                                        <span className="fieldOutput">
                                            {customerData ? getBusinessOrCustomerName(customerData) : ''}
                                        </span>
                                    </div>
                                    <div className="fieldPair">
                                        <span className="fieldLabel">
                                            Email
                                        </span>
                                        <span className="fieldOutput">
                                            {customerData ? customerData.email : ''}
                                        </span>
                                    </div>
                                    <div className="fieldPair">
                                        <span className="fieldLabel">
                                            Phone
                                        </span>
                                        <span className="fieldOutput">
                                            {customerData ? customerData.workPhone : ''}
                                        </span>
                                    </div>
                                    <div className="fieldPair">
                                        <span className="fieldLabel">
                                            Address
                                        </span>
                                        <span className="fieldOutput">
                                            <span className="d-block">
                                                {customerData ? getCustomerAddress() : ''}
                                            </span>
                                            <span className="d-block">
                                                {customerData ? customerData.city + ', ' + customerData.state + ' ' + customerData.postalCode : ''}
                                            </span>
                                        </span>
                                    </div>
                                    <div className="fieldPair">
										<span className="fieldLabel">
											Address Type
										</span>
                                        <span className="fieldOutput">
											{customerData ? customerData.propertyType : ''}
										</span>
                                    </div>
                                    <div className="fieldPair">
										<span className="fieldLabel">
											Contract Number
										</span>
                                        <span className="fieldOutput">
											{jobData ? <Link
                                                            to={`/admin/job-details?customerId=${state.customerId}&jobId=${state.jobId}&propertyAddress=${encodeURIComponent(
                                                                customerData ? customerData.standardizedAddress : ''
                                                            )}&customerName=${encodeURIComponent(
                                                                customerData ? getBusinessOrCustomerName(customerData) : ''
                                                            )}&fromJobDetails=true&propertyType=${encodeURIComponent(customerData ? customerData.propertyType : '')}&homePhone=${encodeURIComponent(customerData ? customerData.homePhone ?? '' : '')}`}
                                                            style={{ color: "blue", textDecoration: "underline", cursor: "pointer" }}
                                                        >
                                                            {jobData.jobId}
                                                        </Link>
                                                : ''}
										</span>
                                    </div>
                                </div>

                                <div className="finalEst p-3">
                                    <div className="bigReveal m-100">
                                        <span className="drumRoll m-100">
                                            Estimate
                                        </span>
                                        <span className="bigNumber m-100">
                                            ${servicesEstimate}
                                        </span>
                                    </div>
                                    <div className="bigReveal m-100 mt-2">
                                        <span className="drumRoll m-100">
                                            Final Price (shown on estimate email)
                                        </span>
                                        <span className="bigNumber m-100">
                                            ${Math.ceil(finalPrice)}
                                        </span>
                                    </div>
                                    <div className="mt-2">
                                        {!isCompletedJob && <button type="button" onClick={() => {
                                            if(jobData.whenCustomerNotified)
                                                setShowNotifyAnyway(true)
                                            else
                                                openEstimatePreview()
                                        }} className="btn btn-primary text-strong px-5 py-2 m-100 w-100">
                                            <i className="far fa-paper-plane"></i> Send Estimate
                                        </button>}
                                        <button type="button" onClick={() => isCompletedJob ? setShowPriceChangeConfirmation(true) : submitEstimate(false)} className={`btn text-strong px-5 py-2 m-100 w-100 ${isCompletedJob ? 'btn-primary' : 'btn-light mt-2'}`}>
                                            <i className="far fa-floppy-disk"></i> {isCompletedJob ? 'Save Changes' : 'Save Estimate'}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                </div>

            </div>
            <NotifyAnywayModal show={showNotifyAnyway} toggle={() => setShowNotifyAnyway(false)} onConfirm={() => { setShowNotifyAnyway(false); openEstimatePreview(); }}/>
            <PreviewEstimateModal show={showPreview} toggle={() => setShowPreview(false)} onConfirm={() => { setShowPreview(false); submitEstimate(true); }} html={previewHtml} failed={previewFailed}/>
            <ConfirmationModal isDanger={true} confirmButtonText={'Yes, save changes'} cardText={'This job already has a DateDone. Updating the pricing now will change the price that was sent to the customer on the invoice and/or the estimate. Are you sure?'} show={showPriceChangeConfirmation} toggle={() => setShowPriceChangeConfirmation(false)} onConfirm={() => submitEstimate(false)}/>
        </>
    )
}
export default EstimateDetailsPage;